import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiZap, FiStar, FiAward, FiShield, FiGift } from 'react-icons/fi';

const Pricing = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsLoggedIn(localStorage.getItem('token') !== null);

    const fetchPlans = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_BASE_URL}/api/subscription/plans`);
        if (!response.ok) throw new Error('Failed to fetch plans');
        const data = await response.json();
        
        // Website shows ONLY 2 plans: 1 Year (₹3,650) and 10 Years (₹25,000)
        // Free Trial, 1 Month, 6 Months, and Mobile App plan are not shown on the website
        const websiteOnlyPlans = data.filter((p) => {
          const name = p.name.toLowerCase();
          return (name.includes('1 year') || name.includes('10 year') || name.includes('10 years')) && !name.includes('mobile');
        });

        // Sort by price
        setPlans(websiteOnlyPlans.sort((a, b) => Number(a.price) - Number(b.price)));
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleAction = () => {
    if (isLoggedIn) {
      navigate('/settings');
    } else {
      navigate('/register');
    }
  };

  // Helper to get nice UI config based on plan name
  const getPlanUIConfig = (plan) => {
    const name = plan.name.toLowerCase();
    
    /* Commented out for website display
    if (name.includes('free trial')) {
      return { color: '#0dcaf0', icon: <FiGift size={28} />, tag: 'NO CREDIT CARD', duration: '7 Days' };
    }
    if (name.includes('1 month')) {
      return { color: '#6c63ff', icon: <FiZap size={28} />, tag: null, duration: '30 Days' };
    }
    if (name.includes('6 month')) {
      return { color: '#d63384', icon: <FiStar size={28} />, tag: 'MOST POPULAR', duration: '180 Days' };
    }
    */
    if (name.includes('1 year')) {
      return { color: '#fd7e14', icon: <FiAward size={28} />, tag: 'BEST VALUE', duration: '1 Year (365 Days)' };
    }
    if (name.includes('10 year') || name.includes('10 years') || name.includes('25 year')) {
      return { color: '#198754', icon: <FiShield size={28} />, tag: 'LIFETIME', duration: '10 Years' };
    }
    
    return { color: '#6c63ff', icon: <FiZap size={28} />, tag: null, duration: plan.duration_days + ' Days' };
  };

  if (loading) {
    return (
      <section className="section pricing__v2" id="pricing">
        <Container className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading plans...</span>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="section pricing__v2" id="pricing" style={{ padding: '80px 0', background: '#f8f9fa' }}>
      <Container>
        <Row className="mb-5">
          <Col md={8} className="text-center mx-auto">
            <span className="subtitle text-uppercase mb-3" style={{ color: '#6c63ff', fontWeight: 700, letterSpacing: 1 }}>PRICING</span>
            <h2 className="mb-3" style={{ fontWeight: 800, color: '#1a1a2e' }}>Find Your Perfect Plan</h2>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Choose the subscription that fits your business. Start for free, upgrade anytime, and unlock powerful features as you grow!</p>
          </Col>
        </Row>

        <Row className="justify-content-center align-items-stretch">
          {plans.map((plan) => {
            const isFree = Number(plan.price) === 0;
            const uiConfig = getPlanUIConfig(plan);
            const isPopular = uiConfig.tag === 'MOST POPULAR';
            const priceValue = Math.floor(Number(plan.price)); // Remove floating value

            // Parse features
            let featuresList = [];
            try {
              const parsed = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features;
              if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
                if (parsed.invoices) featuresList.push('Unlimited Invoices');
                if (parsed.customers) featuresList.push('Unlimited Customers');
                if (parsed.transactions) featuresList.push('Unlimited Transactions');
                if (parsed.reports) featuresList.push('Advanced Reports & Analytics');
                if (parsed.priority_support) featuresList.push('Priority Support');
              } else if (Array.isArray(parsed)) {
                featuresList = parsed;
              }
            } catch (e) { featuresList = []; }

            // Add standard features if missing
            if (featuresList.length === 0 || isFree) {
              featuresList = [
                'Unlimited Invoices',
                'Unlimited Customers',
                'Unlimited Transactions',
                'Advanced Reports & Analytics',
                'Priority Support'
              ];
            }

            return (
              <Col lg={5} md={6} sm={12} className="mb-4" key={plan.id}>
                <div style={{
                  border: isPopular ? `2px solid ${uiConfig.color}` : '1px solid #e9ecef',
                  borderRadius: 20,
                  padding: 28,
                  background: '#fff',
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: isPopular ? `0 8px 32px ${uiConfig.color}22` : '0 2px 12px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                }}>
                  {/* Tag badge */}
                  {uiConfig.tag && (
                    <div style={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: uiConfig.color,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '4px 16px',
                      borderRadius: 20,
                      letterSpacing: 1,
                      whiteSpace: 'nowrap',
                    }}>
                      {uiConfig.tag}
                    </div>
                  )}

                  {/* Icon + Name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14,
                      background: `${uiConfig.color}18`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: uiConfig.color,
                    }}>
                      {uiConfig.icon}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>{plan.name}</div>
                      <div style={{ fontSize: 13, color: '#888' }}>{uiConfig.duration} access</div>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: 38, fontWeight: 800, color: '#1a1a2e' }}>₹{priceValue.toLocaleString('en-IN')}</span>
                    {uiConfig.tag === 'LIFETIME'
                      ? <span style={{ fontSize: 14, color: '#888', marginLeft: 4 }}>one-time</span>
                      : null}
                  </div>

                  {/* Description */}
                  <p style={{ fontSize: 13.5, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
                    {plan.description || "Full access to all premium features."}
                  </p>

                  {/* Features */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
                    {featuresList.map((f, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, fontSize: 13.5, color: '#444' }}>
                        <FiCheck size={14} style={{ color: uiConfig.color, flexShrink: 0 }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  <div className="mt-auto">
                    <button
                      onClick={handleAction}
                      style={{
                        width: '100%', 
                        padding: '13px 0', 
                        borderRadius: 10, 
                        border: isPopular || isFree ? 'none' : `2px solid ${uiConfig.color}`,
                        background: isPopular || isFree ? uiConfig.color : 'transparent',
                        color: isPopular || isFree ? '#fff' : uiConfig.color,
                        fontWeight: 700, 
                        fontSize: 15,
                        cursor: 'pointer',
                        boxShadow: isPopular || isFree ? `0 4px 16px ${uiConfig.color}44` : 'none',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        if (!isPopular && !isFree) {
                          e.target.style.background = uiConfig.color;
                          e.target.style.color = '#fff';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isPopular && !isFree) {
                          e.target.style.background = 'transparent';
                          e.target.style.color = uiConfig.color;
                        }
                      }}
                    >
                      {isLoggedIn 
                        ? (isFree ? 'Go to Dashboard' : 'Upgrade Now') 
                        : (isFree ? 'Start 7 Days Free Trial' : `Get ${plan.name}`)
                      }
                    </button>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

        <p className="text-center text-muted small mt-4 mb-0">
          🔒 Secure payments via Razorpay &nbsp;·&nbsp; All prices include GST &nbsp;·&nbsp; <strong style={{ color: '#dc3545' }}>Strictly Non-Refundable</strong> once activated &nbsp;·&nbsp; <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#6c63ff', textDecoration: 'underline', fontWeight: 600 }}>Terms &amp; Conditions Apply</a>
        </p>
      </Container>
    </section>
  );
};

export default Pricing;